import { useCallback } from 'react';
import html2canvas from 'html2canvas';

export const useDashboardExport = (
  dashboardRef: React.RefObject<HTMLElement>,
  name: string
) => {
  const handleDownload = useCallback((): void => {
    if (!dashboardRef.current) return;

    // Store current scroll position
    const scrollContainer = dashboardRef.current.closest(
      '.scroll-area-viewport'
    ) as HTMLElement | null;
    const originalScrollTop = scrollContainer ? scrollContainer.scrollTop : 0;

    // Reset scroll to capture everything
    if (scrollContainer) {
      scrollContainer.scrollTop = 0;
    }

    // Configure html2canvas with options to capture the full content
    html2canvas(dashboardRef.current, {
      scale: 2, // Higher resolution
      useCORS: true, // Handle cross-origin resources
      allowTaint: true,
      scrollY: 0,
      height: dashboardRef.current.scrollHeight,
      windowHeight: dashboardRef.current.scrollHeight,
      ignoreElements: (element) => {
        return element.classList.contains('no-export');
      },
      onclone: (clonedDocument: Document, element: HTMLElement) => {
        // Make any adjustments to the cloned DOM before capturing if needed
        const clone: HTMLElement = element;

        // Add padding to the clone element
        clone.style.padding = '40px';
        clone.style.backgroundColor = '#ffffff';

        const elementsToHide = clone.querySelectorAll('.no-export');
        elementsToHide.forEach((el: Element) => {
          (el as HTMLElement).style.display = 'none';
        });

        // Use proper null-safe access with non-null assertion operator
        // since we've already checked dashboardRef.current exists
        clone.style.width = `${dashboardRef.current!.scrollWidth}px`;
        clone.style.height = `${dashboardRef.current!.scrollHeight}px`;

        // Ensure all elements are expanded/visible in the clone
        const expandableElements: NodeListOf<Element> = clone.querySelectorAll(
          '.collapsible, .expandable'
        );
        expandableElements.forEach((el: Element) => {
          el.classList.add('expanded');
        });
      },
    })
      .then((canvas: HTMLCanvasElement) => {
        // Create a new canvas with padding
        const paddedCanvas = document.createElement('canvas');
        const padding = 40; // Padding in pixels
        paddedCanvas.width = canvas.width + padding * 2;
        paddedCanvas.height = canvas.height + padding * 2;

        const ctx = paddedCanvas.getContext('2d');
        if (ctx) {
          // Fill with white background
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, paddedCanvas.width, paddedCanvas.height);

          // Draw the original canvas in the center
          ctx.drawImage(canvas, padding, padding);
        }

        // Create download link
        const link = document.createElement('a');
        link.download = `dashboard-${name || 'snapshot'}-${
          new Date().toISOString().split('T')[0]
        }.png`;
        link.href = paddedCanvas.toDataURL('image/png');
        link.click();

        // Restore scroll position
        if (scrollContainer) {
          scrollContainer.scrollTop = originalScrollTop;
        }
      })
      .catch((err: Error) => {
        console.error('Error generating dashboard snapshot:', err);
      });
  }, [dashboardRef, name]);

  return { handleDownload };
};
